import {
    TRIP_HERO_IMAGE_SIZE_LABEL,
    TRIP_HIGHLIGHT_IMAGE_SIZE_LABEL,
} from './utils/tripImageDimensions';

const stepsConfig = {
    // =========================================================================
    // OVERVIEW  (Destination Details)
    // =========================================================================
    overview: {
        title: 'Destination Details',
        description: 'Define the core destination/country information.',
        boxes: [
            {
                title: 'Basic Information',
                description: 'Define the core destination/country information.',
                rows: [
                    ['continent', 'country', 'currency'],
                ],
            },
            {
                title: 'Description',
                description: 'Destination/country overview.',
                rows: [
                    ['description'],
                ],
            },
            {
                title: 'Languages',
                description: 'Commonly spoken languages in the destination.',
                rows: [
                    ['languages'],
                ],
            },
            {
                title: 'Hero Image',
                description: `Hero image for the destination page (recommended ${TRIP_HERO_IMAGE_SIZE_LABEL}; any size accepted).`,
                rows: [
                    ['hero_image'],
                ],
            },
            {
                title: 'Key Highlights',
                description: `Highlighted attractions and places for this destination. Images recommended ${TRIP_HIGHLIGHT_IMAGE_SIZE_LABEL}; any size accepted.`,
                rows: [
                    ['key_highlights'],
                ],
            },
        ],
        fields: {
            continent: {
                label: 'Continent',
                type: 'continent',
                required: true,
            },
            country: {
                label: 'Country',
                type: 'country',
                required: true,
                filterByContinent: true,
            },
            currency: {
                label: 'Currency',
                type: 'currency-select',
                required: false,
                labelFormat: 'codeName',
            },
            description: {
                label: 'Description',
                type: 'html',
                required: false,
                maxLength: 2000,
                placeholder: 'Overview of the destination...',
            },
            languages: {
                label: 'Languages',
                type: 'multi-select',
                required: false,
                optionsSource: 'languages',
            },
            hero_image: {
                label: 'Hero Image',
                type: 'image-upload',
                required: true,
                multiple: false,
                maxFiles: 1,
                accept: 'image/*',
                variant: 'hero',
            },
            key_highlights: {
                label: 'Key Highlights',
                type: 'key-highlights',
                required: false,
            },
        },
    },

    // =========================================================================
    // PACKAGES  (TripPackage — Section A + Section B)
    // Docstring: only Duration*, City*, Status* are required in this step.
    // Package Title/Description/Package Type/Tags/Tier/Sequence are optional.
    // =========================================================================
    packages: {
        title: 'Package Option',
        description: 'Create a bookable package with different pricing, duration, and capacity.',
        boxes: [
            {
                title: 'Package Identity',
                description: '',
                rows: [
                    ['package_title', 'sequence'],
                    ['package_description'],
                    ['tags', 'package_tier'],
                    ['trip_ids'],
                ]
            },
            {
                title: 'Validity & Location',
                description: '',
                rows: [
                    ['duration', 'city'],
                ]
            },
            {
                title: 'Media',
                description: 'Hero image for this package.',
                rows: [
                    ['package_hero_image'],
                ],
            },
            {
                title: 'Gallery',
                description: 'Image gallery for this package.',
                rows: [
                    ['image_gallery'],
                ],
            },
        ],
        fields: {
            package_title: {
                label: 'Package Title',
                type: 'text',
                required: true,
                maxLength: 250,
                placeholder: 'e.g. Standard Package',
                tooltip: 'Optional name for this package option. Shown to customers when you offer more than one package.',
            },
            sequence: {
                label: 'Sequence',
                type: 'number',
                required: false,
                validateMinExclusive: 0,
                validateMinExclusiveMessage: 'Sequence must be greater than 0',
                tooltip: 'Display order on the website. Lower numbers appear first when multiple packages are listed.',
            },
            package_description: {
                label: 'Description',
                type: 'html',
                required: false,
                maxLength: 2000,
                placeholder: 'Describe this package...',
            },
            tags: {
                label: 'Tags',
                type: 'configurable-dropdown',
                required: false,
                optionsEndpointKey: 'package_tags',
                options: [
                    { value: 'best_seller', label: 'Best Seller' },
                    { value: 'trending', label: 'Trending' },
                    { value: 'most_popular', label: 'Most Popular' },
                    { value: 'last_minute_deal', label: 'Last Minute Deal' },
                    { value: 'early_bird', label: 'Early Bird' },
                ],
            },
            package_tier: {
                label: 'Tier',
                type: 'configurable-dropdown',
                required: false,
                optionsEndpointKey: 'package_tier',
                options: [
                    { value: 'budget', label: 'Budget' },
                    { value: 'mid_range', label: 'Mid Range' },
                    { value: 'premium', label: 'Premium' },
                ],
            },
            duration: {
                label: 'Duration (Days)',
                type: 'number',
                required: true,
                validateMinExclusive: 0,
                validateMinExclusiveMessage: 'Duration must be greater than 0',
            },
            city: {
                label: 'Cities',
                type: 'city-autocomplete',
                multiple: true,
                required: true,
            },
            trip_ids: {
                label: 'Linked Trips (Destinations)',
                type: 'trip-selection',
                required: true,
                tooltip: 'Which trip destinations (countries) this package is available on. Must include at least one.',
            },
            package_hero_image: {
                label: 'Package Hero Image',
                type: 'image-upload',
                required: false,
                multiple: false,
                maxFiles: 1,
                accept: 'image/*',
                variant: 'hero',
                attachmentScope: 'package',
            },
            image_gallery: {
                label: 'Gallery',
                type: 'key-highlights',
                required: false,
            },
        },
    },

    // =========================================================================
    // PRICING  (TripPackage — Pricing step)
    // Docstring: Base Price*, Currency*, Payment Template* (multi-select)
    // =========================================================================
    pricing: {
        title: 'Pricing & Billing',
        description: 'Set the package price, trip currency, and how customers can pay for this package. Currency applies to all trip logistics and add-ons.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['base_price', 'currency'],
                ]
            },
            {
                title: '',
                description: '',
                rows: [
                    ['payment_templates'],
                ]
            },
        ],
        fields: {
            base_price: {
                label: 'Base Price',
                type: 'number',
                required: true,
                placeholder: 'e.g. 24999',
                validateMinExclusive: 0,
                validateMinExclusiveMessage: 'Base price must be greater than 0',
                tooltip: 'The standard price of the package per traveller.',
            },
            currency: {
                label: 'Trip Currency',
                type: 'currency-select',
                required: true,
                tooltip: 'Applies to the whole trip — trip logistics, add-ons, and package pricing.',
            },
            payment_templates: {
                label: 'Payment Templates',
                type: 'template-select',
                required: true,
            }
        }
    },

    // =========================================================================
    // SERVICES  (PackageComponent)
    // Docstring: Service Name*, Category*, Sub Category* (depends on Category),
    // Description, Supplier/Merchant*, Cost Price*, Currency*, Status*, plus
    // category-specific fields (Accommodation / Transportation(+Flight) /
    // Activity / Meal / Guide / Other)
    // =========================================================================
    services: {
        title: 'Trip Logistics',
        description: 'Register trip logistics used to deliver this trip. Add one or more logistics items.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['services'],  // single repeatable-form field
                ]
            }
        ],
        fields: {
            services: {
                label: 'Trip Logistics',
                type: 'repeatable-form',
                required: true,
                emptyMessage: 'Please add at least one trip logistic',
                addLabel: 'Add Trip Logistics',
                allowDuplicate: true,
                allowImportFromTrips: true,
                rows: [
                    ['service_name', 'cost_price'],
                    ['category'],
                    ['description'],
                    // ['supplier', 'cost_price', 'currency', 'status'],
                ],
                fields: {
                    service_name: { label: 'Trip Logistics Name', type: 'text', required: true, maxLength: 250 },

                    category: {
                        label: 'Category',
                        type: 'dropdown',
                        required: true,
                        options: [
                            { value: 'accommodation',  label: 'Accommodation' },
                            { value: 'transportation', label: 'Transportation' },
                            { value: 'activity',       label: 'Activity' },
                            { value: 'meal',           label: 'Meal' },
                            { value: 'guide',          label: 'Guide' },
                            { value: 'other',          label: 'Other' },
                        ],
                        // NOTE: showWhen here matches against the *value* of `category`,
                        // not a boolean. StepForm's subField renderer needs to support
                        // this in addition to the boolean checkbox case.
                        subFields: [
                            {
                                showWhen: 'accommodation',
                                rows: [
                                    ['sub_category'],
                                    ['property_name', 'property_address'],
                                    ['check_in_time', 'check_out_time'],
                                    ['min_occupancy', 'max_occupancy'],
                                ],
                                fields: {
                                    sub_category: {
                                        label: 'Sub Category',
                                        type: 'dropdown',
                                        required: true,
                                        options: [
                                            { value: 'dorm', label: 'Dorm' },
                                            { value: 'twin_sharing', label: 'Twin Sharing' },
                                            { value: 'triple_sharing', label: 'Triple Sharing' },
                                            { value: 'private_room', label: 'Private Room' },
                                            { value: 'homestay', label: 'Homestay' },
                                            { value: 'camping', label: 'Camping' },
                                        ],
                                    },
                                    property_name: { label: 'Property Name',    type: 'text',   required: false },
                                    property_address: { label: 'Property Address', type: 'address-autocomplete',   required: false },
                                    check_in_time: {
                                        label: 'Check In Time',
                                        type: 'time',
                                        required: true,
                                    },
                                    check_out_time: {
                                        label: 'Check Out Time',
                                        type: 'time',
                                        required: true,
                                    },
                                    min_occupancy: {
                                        label: 'Min Occupancy',
                                        type: 'number',
                                        required: true,
                                        validateMinExclusive: 0,
                                        validateMinExclusiveMessage: 'Min occupancy must be greater than 0',
                                    },
                                    max_occupancy: {
                                        label: 'Max Occupancy',
                                        type: 'number',
                                        required: true,
                                        validateGreaterThanOrEqual: 'min_occupancy',
                                        validateGreaterThanOrEqualMessage: 'Max occupancy must be greater than or equal to min occupancy',
                                    },
                                }
                            },
                            {
                                showWhen: 'transportation',
                                rows: [
                                    ['sub_category'],
                                    ['pickup_location', 'drop_location'],
                                    ['pickup_time', 'drop_time'],
                                    ['min_passenger', 'max_passenger'],
                                ],
                                fields: {
                                    sub_category: {
                                        label: 'Sub Category',
                                        type: 'dropdown',
                                        required: true,
                                        options: [
                                            { value: 'airport_transfer', label: 'Airport Transfer' },
                                            { value: 'flight',           label: 'Flight' },
                                            { value: 'train',            label: 'Train' },
                                            { value: 'bus',              label: 'Bus' },
                                            { value: 'ferry',            label: 'Ferry' },
                                            { value: 'cab',              label: 'Cab' },
                                            { value: 'car_rental',       label: 'Car Rental' },
                                            { value: 'bike_rental',      label: 'Bike Rental' },
                                        ],
                                        // Flight-only extra fields
                                        subFields: [
                                            {
                                                showWhen: 'flight',
                                                rows: [
                                                    ['airline', 'flight_number'],
                                                    ['departure_airport', 'arrival_airport'],
                                                    ['departure_time', 'arrival_time'],
                                                    ['baggage_included'],
                                                ],
                                                fields: {
                                                    airline: {
                                                        label: 'Airline',
                                                        type: 'text',
                                                        required: false
                                                    },
                                                    flight_number: {
                                                        label: 'Flight Number',
                                                        type: 'text',
                                                        required: false
                                                    },
                                                    departure_airport: {
                                                        label: 'Departure Airport',
                                                        type: 'text',
                                                        required: false
                                                    },
                                                    arrival_airport: {
                                                        label: 'Arrival Airport',
                                                        type: 'text',
                                                        required: false
                                                    },
                                                    departure_time: {
                                                        label: 'Departure Time',
                                                        type: 'time',
                                                        required: false
                                                    },
                                                    arrival_time: {
                                                        label: 'Arrival Time',
                                                        type: 'time',
                                                        required: false,
                                                    },
                                                    baggage_included: {
                                                        label: 'Baggage Included',
                                                        type: 'checkbox',
                                                        required: false
                                                    },
                                                }
                                            }
                                        ]
                                    },
                                    pickup_location:
                                        {
                                            label: 'Pickup Location',
                                            type: 'address-autocomplete',
                                            required: false
                                        },
                                    drop_location:
                                        {
                                            label: 'Drop Location',
                                            type: 'address-autocomplete',
                                            required: false
                                        },
                                    pickup_time:
                                        {
                                            label: 'Pickup Time',
                                            type: 'time',
                                            required: false
                                        },
                                    drop_time:
                                        {
                                            label: 'Drop Time',
                                            type: 'time',
                                            required: false,
                                        },
                                    min_passenger:
                                        {
                                            label: 'Min Passengers',
                                            type: 'number',
                                            required: false,
                                            validateMinExclusive: 0,
                                            validateMinExclusiveMessage: 'Minimum passenger must be greater than 0',
                                        },
                                    max_passenger: {
                                        label: 'Max Passengers',
                                        type: 'number',
                                        required: false,
                                        validateGreaterThanOrEqual: 'min_passenger',
                                        validateGreaterThanOrEqualMessage: 'Maximum Passengers must be greater than or equal to Minimum Passengers',
                                    },
                                }
                            },
                            {
                                showWhen: 'activity',
                                rows: [
                                    ['sub_category'],
                                    ['activity_location'],
                                    ['start_time', 'end_time', 'duration'],
                                    ['minimum_participants', 'maximum_participants'],
                                ],
                                fields: {
                                    sub_category: {
                                        label: 'Sub Category',
                                        type: 'dropdown',
                                        required: true,
                                        options: [
                                            { value: 'sightseeing',         label: 'Sightseeing' },
                                            { value: 'trek',                label: 'Trek' },
                                            { value: 'adventure_activity',  label: 'Adventure Activity' },
                                            { value: 'water_activity',      label: 'Water Activity' },
                                            { value: 'cultural_experience', label: 'Cultural Experience' },
                                        ],
                                    },
                                    activity_location: {
                                        label: 'Activity Location',
                                        type: 'address-autocomplete',
                                        required: false
                                    },
                                    start_time: {
                                        label: 'Start Time',
                                        type: 'time',
                                        required: false
                                    },
                                    end_time: {
                                        label: 'End Time',
                                        type: 'time',
                                        required: false,
                                    },
                                    duration: {
                                        label: 'Duration',
                                        type: 'duration-calc',
                                        required: false,
                                    },
                                    minimum_participants: {
                                        label: 'Min Participants',
                                        type: 'number',
                                        required: false,
                                        validateMinExclusive: 0,
                                        validateMinExclusiveMessage: 'Minimum participants must be greater than 0',
                                    },
                                    maximum_participants: {
                                        label: 'Max Participants',
                                        type: 'number',
                                        required: false,
                                        validateGreaterThanOrEqual: 'minimum_participants',
                                        validateGreaterThanOrEqualMessage: 'Maximum participants must be greater than or equal to minimum participants',
                                    },
                                }
                            },
                            {
                                showWhen: 'meal',
                                rows: [
                                    ['sub_category'],
                                    ['venue', 'service_time'],
                                ],
                                fields: {
                                    sub_category: {
                                        label: 'Sub Category',
                                        type: 'dropdown',
                                        required: true,
                                        options: [
                                            { value: 'breakfast', label: 'Breakfast' },
                                            { value: 'lunch',     label: 'Lunch' },
                                            { value: 'dinner',    label: 'Dinner' },
                                            { value: 'snacks',    label: 'Snacks' },
                                        ],
                                    },
                                    venue:        { label: 'Venue',        type: 'address-autocomplete', required: false },
                                    service_time: { label: 'Service Time', type: 'time', required: false },
                                }
                            },
                            {
                                showWhen: 'guide',
                                rows: [
                                    ['sub_category'],
                                    ['guide_name', 'contact_number'],
                                    ['languages', 'reporting_time'],
                                    ['meeting_point'],
                                ],
                                fields: {
                                    sub_category: {
                                        label: 'Sub Category',
                                        type: 'dropdown',
                                        required: true,
                                        options: [
                                            { value: 'tour_guide',   label: 'Tour Guide' },
                                            { value: 'trek_leader',  label: 'Trek Leader' },
                                            { value: 'local_expert', label: 'Local Expert' },
                                        ],
                                    },
                                    guide_name: {
                                        label: 'Guide Name',
                                        type: 'text',
                                        required: false
                                    },
                                    contact_number: {
                                        label: 'Contact Number',
                                        type: 'phone',
                                        required: false,
                                        validatePhone: true,
                                        validatePhoneMessage: 'Enter a valid contact number',
                                    },
                                    languages: {
                                        label: 'Languages',
                                        type: 'dropdown',
                                        required: false,
                                        options: [
                                            { value: 'english', label: 'English' },
                                            { value: 'hindi', label: 'Hindi' },
                                            { value: 'french', label: 'French' },
                                            { value: 'spanish', label: 'Spanish' },
                                            { value: 'german', label: 'German' },
                                            { value: 'italian', label: 'Italian' },
                                            { value: 'mandarin', label: 'Mandarin' },
                                            { value: 'arabic', label: 'Arabic' },
                                            { value: 'japanese', label: 'Japanese' },
                                            { value: 'portuguese', label: 'Portuguese' },
                                        ],
                                    },
                                    reporting_time: {
                                        label: 'Reporting Time',
                                        type: 'time',
                                        required: false
                                    },
                                    meeting_point: {
                                        label: 'Meeting Point',
                                        type: 'address-autocomplete',
                                        required: false
                                    },
                                }
                            },
                            {
                                showWhen: 'other',
                                rows: [
                                    ['sub_category'],
                                ],
                                fields: {
                                    sub_category: {
                                        label: 'Sub Category',
                                        type: 'dropdown',
                                        required: true,
                                        options: [
                                            { value: 'permit',        label: 'Permit' },
                                            { value: 'insurance',     label: 'Insurance' },
                                            { value: 'miscellaneous', label: 'Miscellaneous' },
                                        ],
                                    },
                                }
                            },
                        ]
                    },

                    description: { label: 'Description', type: 'html', required: false, maxLength: 2000 },

                    // Points to a user id (merchant/supplier) — resolved via a dynamic
                    // user picker, same pattern as payment_templates / hss.
                    // supplier: { label: 'Supplier / Merchant', type: 'text', required: true },

                    cost_price: {
                        label: 'Cost Price',
                        type: 'number',
                        required: true,
                        validateMin: 0,
                        validateMinMessage: 'Cost price must be 0 or greater',
                    },

                    currency: {
                        label: 'Currency',
                        type: 'currency-select',
                        required: true,
                        useTripCurrency: true,
                    },
                },
            },
        },
    },

    // =========================================================================
    // DEPARTURES  (DepartureDate)
    // Docstring: Departure Date*, Return Date*, Departure City, Status*,
    // Total Allocation*, Min Pax*, Max Pax*, Book By Date*,
    // Offer (dropdown: none/early bird/last minute) -> shared additional fields,
    // HSS Template dropdown -> auto fields
    // =========================================================================
    departures: {
        title: 'Departure Dates',
        description: 'Schedule when customers can travel and manage availability for each package. Add one or more departure dates.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['departures'],
                ]
            }
        ],
        fields: {
            departures: {
                label: 'Departure',
                type: 'repeatable-form',
                required: true,
                emptyMessage: 'Please add at least one departure',
                addLabel: 'Add Departure',
                allowDuplicate: true,
                rows: [
                    ['departure_date', 'return_date'],
                    ['departure_city', 'status'],
                    ['total_allocation', 'minimum_pax'],
                    ['book_by_date'],
                    ['offer'],
                    ['hss'],
                ],
                fields: {
                    departure_date: {
                        label: 'Departure Date',
                        type: 'date',
                        required: true,
                        disallowPast: true,
                        disallowPastMessage: 'Departure date cannot be in the past',
                    },
                    return_date: {
                        label: 'Return Date',
                        type: 'date',
                        required: true,
                        tooltip: 'Auto-filled from package duration when you set the departure date. Must match the total trip length.',
                        disallowPast: true,
                        disallowPastMessage: 'Return date cannot be in the past',
                        validateOnOrAfter: 'departure_date',
                        validateOnOrAfterMessage: 'Return date must not be earlier than the departure date',
                    },
                    departure_city: { label: 'Departure City', type: 'city-autocomplete', required: false, multiple: false },
                    status: {
                        label: 'Departure Status',
                        type: 'configurable-dropdown',
                        required: true,
                        optionsEndpointKey: 'departure_status',
                        tooltip: 'Booking availability for this departure. Only Open or Early Bird departures accept new bookings.',
                        options: [
                            { value: 'draft',    label: 'Draft' },
                            { value: 'early_bird',   label: 'Early Bird' },
                            { value: 'open', label: 'Open' },
                            { value: 'fast_selling', label: 'Fast Selling (60% Booked)' },
                            { value: 'sold_out', label: 'Sold out' },
                            { value: 'completed', label: 'Completed' },
                            { value: 'cancelled', label: 'Cancelled' },
                        ],
                    },
                    total_allocation: {
                        label: 'Maximum Pax',
                        type: 'number',
                        required: true,
                        validateMinExclusive: 0,
                        validateMinExclusiveMessage: 'Maximum pax must be greater than 0',
                    },
                    minimum_pax: {
                        label: 'Minimum Pax',
                        type: 'number',
                        required: false,
                        validateMinExclusive: 0,
                        validateMinExclusiveMessage: 'Minimum pax must be greater than 0',
                        validateLessThan: 'total_allocation',
                        validateLessThanMessage: 'Minimum pax must be less than or equal to Maximum pax.',
                    },
                    book_by_date: {
                        label: 'Book By Date',
                        type: 'date',
                        required: true,
                        disallowPast: true,
                        disallowPastMessage: 'Book by date cannot be in the past',
                        validateOnOrBefore: 'departure_date',
                        validateOnOrBeforeMessage: 'Book by date must not be later than departure date',
                    },

                    // Docstring: single "Offer" dropdown (default None), with ONE shared
                    // set of additional fields (start/end date, discount %, discount amount)
                    // shown for whichever offer is chosen.
                    offer: {
                        label: 'Offer',
                        type: 'dropdown',
                        default: 'none',
                        required: true,
                        options: [
                            { value: 'none',         label: 'None' },
                            { value: 'early_bird',   label: 'Early Bird' },
                            { value: 'last_minute',  label: 'Last Minute Deal' },
                        ],
                        subFields: [
                            {
                                showWhen: 'none',
                                rows: [
                                    ['discount_calc'],
                                ],
                                fields: {
                                    discount_calc: {
                                        label: 'Discount',
                                        type: 'discount-calc',
                                        required: false,
                                        tooltips: {
                                            percentage: 'Discount percentage applied to the base price.',
                                            amount: 'Auto-calculated discount amount based on the percentage entered.',
                                        },
                                    },
                                }
                            },
                            {
                                showWhen: 'early_bird',
                                rows: [
                                    ['early_bird_start_date', 'early_bird_end_date'],
                                    ['discount_calc'],
                                ],
                                fields: {
                                    early_bird_start_date: {
                                        label: 'Start Date',
                                        type: 'date',
                                        required: true,
                                        disallowPast: true,
                                        disallowPastMessage: 'Early bird start date cannot be in the past',
                                        validateOnOrBefore: 'departure_date',
                                        validateOnOrBeforeMessage: 'Early bird start date must not be later than the departure date',
                                    },
                                    early_bird_end_date: {
                                        label: 'End Date',
                                        type: 'date',
                                        required: true,
                                        disallowPast: true,
                                        disallowPastMessage: 'Early bird end date cannot be in the past',
                                        validateOnOrAfter: 'early_bird_start_date',
                                        validateOnOrAfterMessage: 'End date must not be earlier than the start date',
                                        validateOnOrBefore: 'departure_date',
                                        validateOnOrBeforeMessage: 'Early bird end date must not be later than the departure date',
                                    },
                                    discount_calc: {
                                        label: 'Discount',
                                        type: 'discount-calc',
                                        required: false
                                    },
                                }
                            },
                            {
                                showWhen: 'last_minute',
                                rows: [
                                    ['last_minute_start_date', 'last_minute_end_date'],
                                    ['discount_calc'],
                                ],
                                fields: {
                                    last_minute_start_date: {
                                        label: 'Start Date',
                                        type: 'date',
                                        required: true,
                                        disallowPast: true,
                                        disallowPastMessage: 'Last minute start date cannot be in the past',
                                        validateOnOrBefore: 'departure_date',
                                        validateOnOrBeforeMessage: 'Last minute start date must not be later than the departure date',
                                    },
                                    last_minute_end_date: {
                                        label: 'End Date',
                                        type: 'date',
                                        required: true,
                                        disallowPast: true,
                                        disallowPastMessage: 'Last minute end date cannot be in the past',
                                        validateOnOrAfter: 'last_minute_start_date',
                                        validateOnOrAfterMessage: 'End date must not be earlier than the start date',
                                        validateOnOrBefore: 'departure_date',
                                        validateOnOrBeforeMessage: 'Last minute end date must not be later than the departure date',
                                    },
                                    discount_calc: {
                                        label: 'Discount',
                                        type: 'discount-calc',
                                        required: false
                                    },
                                }
                            },
                        ]
                    },

                    // HSS Template dropdown -> reveals HSS %, flat charge, and
                    // auto-calculated total amount. Handled by the custom widget.
                    hss: {
                        label: 'High Season Surcharge (HSS)',
                        type: 'hss-select',
                        required: false,
                        tooltip: 'Optional surcharge applied during peak travel periods.',
                        tooltips: {
                            template: 'Pick a pre-configured HSS rate, or choose Custom to enter your own.',
                            percentage: 'Percentage of the base price added as surcharge.',
                            flatCharge: 'A fixed amount added on top of the percentage-based surcharge.',
                            amount: 'Auto-calculated total surcharge based on percentage + flat charge.',
                        },
                    },
                },
            },
        },
    },

    // =========================================================================
    // ALLOCATIONS  (MerchantAllocation)
    // After Departures: allocate merchants to a package departure / logistics item.
    // =========================================================================
    allocations: {
        title: 'Merchant Allocations',
        description:
            'Select trip logistics, then a merchant, then assign one or more departure dates and complete the allocation details.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['allocations'],
                ],
            },
        ],
        fields: {
            allocations: {
                label: 'Merchant Allocation',
                type: 'repeatable-form',
                required: false,
                addLabel: 'Add Merchant Allocation',
                allowDuplicate: true,
                // Progressive UX: Trip Logistics → Merchant → remaining UI fields.
                // Package Options, Rejection Reason, Rejected On are details-page only.
                rows: [
                    ['package_component_id'],
                ],
                fields: {
                    package_component_id: {
                        label: 'Trip Logistics',
                        type: 'searchable-lookup',
                        required: true,
                        optionsSource: 'trip_logistics',
                        placeholder: 'Search trip logistics for this destination...',
                        tooltip: 'Only trip logistics created for this destination (current wizard step or already saved on this trip).',
                        subFields: [
                            {
                                showWhen: '__has_value__',
                                rows: [
                                    ['merchant_id'],
                                ],
                                fields: {
                                    merchant_id: {
                                        label: 'Merchant',
                                        type: 'searchable-lookup',
                                        required: true,
                                        optionsSource: 'merchants',
                                        filterMerchantsByLogistics: 'package_component_id',
                                        placeholder: 'Search matching or DMC merchants...',
                                        tooltip: 'Shows merchants whose service type matches this trip logistics, plus any merchant with DMC.',
                                        subFields: [
                                            {
                                                showWhen: '__has_value__',
                                                rows: [
                                                    ['name'],
                                                    ['departure_date_ids'],
                                                    ['status', 'pricing_model'],
                                                    ['allocation_priority', 'currency'],
                                                    ['cost'],
                                                    ['min_number'],
                                                ],
                                                fields: {
                                                    name: {
                                                        label: 'Merchant Allocation Name',
                                                        type: 'text',
                                                        required: true,
                                                        maxLength: 255,
                                                    },
                                                    status: {
                                                        label: 'Status',
                                                        type: 'dropdown',
                                                        required: true,
                                                        default: 'Pending',
                                                        options: [
                                                            { value: 'Pending', label: 'Pending' },
                                                            { value: 'Accepted', label: 'Accepted' },
                                                            { value: 'Rejected', label: 'Rejected' },
                                                        ],
                                                    },
                                                    pricing_model: {
                                                        label: 'Pricing Model',
                                                        type: 'dropdown',
                                                        required: true,
                                                        default: 'Dated',
                                                        options: [
                                                            { value: 'Dated', label: 'Dated' },
                                                            { value: 'PerPax', label: 'Per Pax' },
                                                            { value: 'PerRoom', label: 'Per Room' },
                                                        ],
                                                        subFields: [
                                                            {
                                                                showWhen: 'Dated',
                                                                rows: [
                                                                    ['season_from', 'season_to'],
                                                                ],
                                                                fields: {
                                                                    season_from: {
                                                                        label: 'Date From',
                                                                        type: 'date',
                                                                        required: true,
                                                                        tooltip:
                                                                            'Must fall within the selected departure dates.',
                                                                        disallowPast: true,
                                                                        disallowPastMessage: 'Date From cannot be in the past',
                                                                        minFromField: 'season_bound_from',
                                                                        maxFromField: 'season_bound_to',
                                                                        validateOnOrBefore: 'season_to',
                                                                        validateOnOrBeforeMessage:
                                                                            'Date From must not be later than Date To',
                                                                    },
                                                                    season_to: {
                                                                        label: 'Date To',
                                                                        type: 'date',
                                                                        required: true,
                                                                        tooltip:
                                                                            'Must fall within the selected departure dates.',
                                                                        disallowPast: true,
                                                                        disallowPastMessage: 'Date To cannot be in the past',
                                                                        minFromField: 'season_bound_from',
                                                                        maxFromField: 'season_bound_to',
                                                                        validateOnOrAfter: 'season_from',
                                                                        validateOnOrAfterMessage:
                                                                            'Date To must not be earlier than Date From',
                                                                    },
                                                                },
                                                            },
                                                        ],
                                                    },
                                                    allocation_priority: {
                                                        label: 'Allocation Priority',
                                                        type: 'number',
                                                        required: false,
                                                        default: 0,
                                                        min: 0,
                                                    },
                                                    currency: {
                                                        label: 'Currency',
                                                        type: 'currency-select',
                                                        required: true,
                                                        default: 'GBP',
                                                        labelFormat: 'codeName',
                                                    },
                                                    cost: {
                                                        label: 'Merchant Cost',
                                                        type: 'number',
                                                        required: true,
                                                        min: 0,
                                                    },
                                                    departure_date_ids: {
                                                        label: 'Departure Dates',
                                                        type: 'multi-select',
                                                        required: true,
                                                        optionsSource: 'departure_dates',
                                                        placeholder: 'Select one or more departure dates…',
                                                        tooltip:
                                                            'Each selected departure is saved as its own allocation. For Dated pricing, Date From / Date To must sit within these departure dates.',
                                                    },
                                                    min_number: {
                                                        label: 'Requested Allocation',
                                                        type: 'number',
                                                        required: false,
                                                        min: 0,
                                                        tooltip:
                                                            'Auto-filled from merchant inventory and departure allocation when inventory exists; otherwise enter manually.',
                                                    },
                                                },
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    },
                },
            },
        },
    },

    // =========================================================================
    // ITINERARIES  (Itinerary + ItineraryDay + ItineraryDayLineItem)
    // Docstring: Title*, Description, Primary attractions, Meal Plan, Inclusion,
    // Exclusion, Status; per-day: Day title, Guide Instructions, Description,
    // Line Items with Item Title*, Trip Logistics*, Location, Start/End,
    // Min/Max Pax, Sequence* (no duplicates), Mandatory, Description
    // =========================================================================
    itineraries: {
        title: 'Itinerary Builder',
        description: 'Construct day-by-day schedules and attach registered services.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['itinerary_title'],
                    // ['itinerary_description'],
                    // ['primary_attraction', 'meal_plan'],
                    ['inclusion'],
                    ['exclusion'],
                ]
            },
            // Second box is handled separately in StepForm as a dynamic day builder
            // driven by formData.packages.duration — not declared in rows here
        ],
        fields: {
            itinerary_title: { label: 'Itinerary Title', type: 'text', required: true, maxLength: 250 },
            // itinerary_description: { label: 'Description', type: 'html', required: false, maxLength: 2000 },
            // primary_attraction: { label: 'Primary Attraction', type: 'text', required: false },
            // meal_plan: {
            //     label: 'Meal Plan', type: 'dropdown', required: false,
            //     options: [
            //         { value: 'no_meals', label: 'No Meals' },
            //         { value: 'breakfast_only', label: 'Breakfast Only' },
            //         { value: 'breakfast_and_dinner', label: 'Breakfast and Dinner' },
            //         { value: 'breakfast_lunch_dinner', label: 'Breakfast, Lunch and Dinner' },
            //         { value: 'all_inclusive', label: 'All Inclusive' },
            //     ],
            // },
            inclusion: { label: 'Inclusion', type: 'html', required: false },
            exclusion: { label: 'Exclusion', type: 'html', required: false },
        },
        // Line item fields used by the day builder inside StepForm
        lineItemFields: {
            item_title: { label: 'Item Title', type: 'text', required: true, maxLength: 250 },
            trip_component_map: { label: 'Trip Logistics', type: 'component-select', required: true },
            location: { label: 'Location', type: 'address-autocomplete', required: false },
            // start_date_time: {
            //     label: 'Start Date & Time',
            //     type: 'datetime',
            //     required: false
            // },
            // end_date_time: {
            //     label: 'End Date & Time',
            //     type: 'datetime',
            //     required: false,
            //     validateAfter: 'start_date_time',
            //     validateAfterMessage: 'End date must be after start date',
            // },
            sequence: {
                label: 'Sequence',
                type: 'number',
                required: true,
                min: 1,
                default: 1,
                validateMinExclusive: 0,
                validateMinExclusiveMessage: 'Sequence must be greater than 0',
            },
            day_line_item_description: { label: 'Notes', type: 'html', required: false },
        }
    },

    // =========================================================================
    // ADDONS  (Addon)
    // Arrive Early / Stay Behind: Extension Days schedule with day line items.
    // Other types: optional attach trip logistics + specific-day scope.
    // =========================================================================
    addons: {
        title: 'Add-On Configuration',
        description: 'Configure optional upgrades, experiences, and trip extensions.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['addons'],
                ]
            }
        ],
        // Reused by AddonExtensionScheduleField (same shape as itineraries.lineItemFields)
        lineItemFields: {
            item_title: { label: 'Item Title', type: 'text', required: true, maxLength: 250 },
            trip_component_map: { label: 'Trip Logistics', type: 'component-select', required: true },
            location: { label: 'Location', type: 'address-autocomplete', required: false },
            sequence: {
                label: 'Sequence',
                type: 'number',
                required: true,
                min: 1,
                default: 1,
                validateMinExclusive: 0,
                validateMinExclusiveMessage: 'Sequence must be greater than 0',
            },
            day_line_item_description: { label: 'Notes', type: 'html', required: false },
        },
        fields: {
            addons: {
                label: 'Add-On',
                type: 'repeatable-form',
                required: false,
                addLabel: 'Add Add-On',
                allowDuplicate: true,
                rows: [
                    ['addon_name', 'website_title'],
                    ['website_description'],
                    ['addon_type', 'application_scope'],
                    ['addon_price', 'pricing_basis'],
                    ['status'],
                    ['min_quantity', 'max_quantity'],
                ],
                fields: {
                    addon_name: {
                        label: 'Add-on Name',
                        type: 'text',
                        required: true,
                        maxLength: 250,
                    },
                    website_title: {
                        label: 'Website Title',
                        type: 'text',
                        required: true,
                        maxLength: 250,
                    },
                    website_description: {
                        label: 'Website Description',
                        type: 'html',
                        required: false,
                        maxLength: 2000,
                    },
                    addon_type: {
                        label: 'Add-on Type',
                        type: 'dropdown',
                        tooltip: [
                            'Activity: Tours & experiences',
                            'Upgrade: Room & vehicle upgrades',
                            'Arrive Early: Pre-trip extension days',
                            'Stay Behind: Post-trip extension days',
                            'Service: Transfers & insurance',
                        ].join('\n'),
                        required: true,
                        options: [
                            { label: 'Service', value: 'service_addon' },
                            { label: 'Activity', value: 'activity_addon' },
                            { label: 'Upgrade', value: 'upgrade_addon' },
                            { label: 'Arrive Early', value: 'arrive_early' },
                            { label: 'Stay Behind', value: 'stay_behind' },
                        ],
                        subFields: [
                            {
                                showWhen: 'arrive_early',
                                fullWidth: true,
                                rows: [
                                    ['addon_itinerary_days'],
                                ],
                                fields: {
                                    addon_itinerary_days: {
                                        label: 'Number of Days',
                                        type: 'addon-extension-schedule',
                                        required: true,
                                    },
                                },
                            },
                            {
                                showWhen: 'stay_behind',
                                fullWidth: true,
                                rows: [
                                    ['addon_itinerary_days'],
                                ],
                                fields: {
                                    addon_itinerary_days: {
                                        label: 'Number of Days',
                                        type: 'addon-extension-schedule',
                                        required: true,
                                    },
                                },
                            },
                            {
                                showWhen: 'service_addon',
                                rows: [['linked_component_id']],
                                fields: {
                                    linked_component_id: {
                                        label: 'Attach Trip Logistics',
                                        type: 'component-select',
                                        required: false,
                                    },
                                },
                            },
                            {
                                showWhen: 'activity_addon',
                                rows: [['linked_component_id']],
                                fields: {
                                    linked_component_id: {
                                        label: 'Attach Trip Logistics',
                                        type: 'component-select',
                                        required: false,
                                    },
                                },
                            },
                            {
                                showWhen: 'upgrade_addon',
                                rows: [['linked_component_id']],
                                fields: {
                                    linked_component_id: {
                                        label: 'Attach Trip Logistics',
                                        type: 'component-select',
                                        required: false,
                                    },
                                },
                            },
                        ],
                    },
                    application_scope: {
                        label: 'Applies to',
                        type: 'dropdown',
                        required: true,
                        options: [
                            { value: 'entire_trip',  label: 'Entire Trip' },
                            { value: 'specific_day', label: 'Specific Day' },
                            { value: 'before_trip',  label: 'Before Trip' },
                            { value: 'after_trip',   label: 'After Trip' },
                        ],
                        subFields: [
                            {
                                showWhen: 'specific_day',
                                rows: [
                                    ['itinerary_day_id'],
                                ],
                                fields: {
                                    itinerary_day_id: {
                                        label: 'Day',
                                        type: 'day-select',
                                        required: true,
                                    },
                                }
                            },
                        ]
                    },
                    addon_price: {
                        label: 'Price',
                        type: 'number',
                        required: true,
                        validateMinExclusive: 0,
                        validateMinExclusiveMessage: 'Price must be greater than 0',
                    },
                    pricing_basis: {
                        label: 'Pricing Basis',
                        type: 'dropdown',
                        required: true,
                        tooltip: 'Per Passenger multiplies price by selected travelers. Fixed charges the price once.',
                        options: [
                            { label: 'Per Passenger', value: 'per_passenger' },
                            { label: 'Fixed', value: 'fixed' },
                        ],
                    },
                    status: {
                        label: 'Status',
                        type: 'dropdown',
                        required: true,
                        options: [
                            { label: 'Draft', value: 'draft' },
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' },
                        ],
                    },
                    currency: {
                        label: 'Currency',
                        type: 'currency-select',
                        required: true,
                        useTripCurrency: true,
                    },
                    min_quantity: {
                        label: 'Min Pax',
                        type: 'number',
                        required: true,
                        validateMinExclusive: 0,
                        validateMinExclusiveMessage: 'Min Pax must be greater than 0',
                    },
                    max_quantity: {
                        label: 'Max Pax',
                        type: 'number',
                        required: true,
                        validateGreaterThanOrEqual: 'min_quantity',
                        validateGreaterThanOrEqualMessage: 'Max Pax must be greater than or equal to Min Pax',
                    },
                    linked_component_id: {
                        label: 'Attach Trip Logistics',
                        type: 'component-select',
                        required: false,
                    },
                }
            },
        },
    },

    // =========================================================================
    // FAQS  (FAQ)
    // Docstring: Question*, Answer*, Published*
    // =========================================================================
    faqs: {
        title: 'FAQs',
        description: 'Populate common queries to aid customer conversion on the frontend.',
        boxes: [
            {
                title: '',
                description: '',
                rows: [
                    ['faqs'],
                ]
            }
        ],
        fields: {
            faqs: {
                label: 'FAQ',
                type: 'repeatable-form',
                required: false,
                addLabel: 'Add FAQs',
                allowDuplicate: true,
                rows: [
                    ['question'],
                    ['answer'],
                    ['active'],
                ],
                fields: {
                    question: {
                        label: 'Question',
                        type: 'text',
                        required: true,
                    },
                    answer: {
                        label: 'Answer',
                        type: 'text',
                        required: true,
                    },
                    active: {
                        label: 'Published',
                        type: 'checkbox',
                        required: true,
                    },
                }
            }
        }
    }
};

export default stepsConfig;